import emojiRender from '_lib/emoji/EmojiRender';
import markdownToHtml from '_lib/MarkdownToHtml.js';
import {
  Article,
  CardDelegate,
  PreproccessDelegate,
  DEFAULT_BASE_DELEGATE,
  handleOriginSource,
  handleOriginalNews,
  handleNewsSource
} from './article/index';

// 导入卡片组件
import GameCards from './components/cards/game-card/com-index';
import SkuCards from './components/cards/sku-card/com-index';
import WikiCards from './components/cards/wiki-card/com-index';
import AlbumCards from './components/cards/album-card/com-index';
import LeagueCards from './components/cards/league-card/com-index';
import MatchCards from './components/cards/match-card/com-index';
import AwardTableCards from './components/cards/award-table-card/com-index';
import ChatRoomCards from './components/cards/chatroom-card/com-index';
import Dota2MatchRecordCards from './components/cards/dota2-match-record-card/com-index';
import LOLMatchRecordCards from './components/cards/lol-match-record-card/com-index';

/**
 * Post 组件数据初始化和加工工具类
 */
class PostDataUtils {
  /**
   * 处理文章内容的 emoji 转换
   * 将 <span data-emoji="xxx" class="hb-emoji"></span> 转换为 [xxx]
   * @param {Array} content - 文章内容数组
   * @returns {Array} 处理后的内容数组
   */
  static processArticleContentEmoji(content = []) {
    return content.map(item => {
      if (item.type === 'text') {
        return {
          ...item,
          text: item.text.replace(/<span data-emoji="([^"]+)" class="hb-emoji"><\/span>/g, '[$1]')
        };
      }
      return item;
    });
  }

  /**
   * 处理帖子标题的 emoji 转换
   * @param {string} title - 原始标题
   * @returns {Object} { origin_title, title }
   */
  static processLinkTitle(title = '') {
    return {
      origin_title: title,
      title: emojiRender.replaceEmojiByTextName(title)
    };
  }

  /**
   * 处理文章类型的帖子数据
   * @param {Object} result - API 返回的结果
   * @returns {Object} 处理后的数据对象
   */
  static processArticleLinkData(result) {
    const link = result.link;
    if (!link) {
      return null;
    }

    // 处理内容 emoji
    const processedContent = this.processArticleContentEmoji(link.content || []);
    
    // 处理标题
    const titleData = this.processLinkTitle(link.title);

    // 处理用户信息
    const current_user = result.current_user || {};
    const awardInfo = {
      isAward: current_user.is_award_link,
      awardCount: link.up
    };
    const favInfo = {
      isFav: current_user.is_favour_link,
      favCount: 0
    };

    return {
      link: {
        ...link,
        content: processedContent,
        ...titleData
      },
      awardInfo,
      favInfo,
      is_video: false
    };
  }

  /**
   * 处理普通帖子（非文章）的内容格式
   * @param {Object} link - 帖子数据
   * @param {boolean} is_video - 是否为视频
   * @returns {Object} 处理后的帖子数据
   */
  static processNormalLinkContent(link, is_video) {
    const processedLink = { ...link };

    if (is_video) {
      // 视频类型：将 text 转换为 content 格式
      processedLink.content = [{
        type: 'text',
        text: link.text
      }];
    } else {
      // 普通类型：解析 text JSON
      processedLink.content = link.text ? JSON.parse(link.text) : '';
    }

    // 删除原始 text 字段
    delete processedLink.text;

    // 处理标题
    const titleData = this.processLinkTitle(processedLink.title);
    Object.assign(processedLink, titleData);

    return processedLink;
  }

  /**
   * 处理普通帖子（非文章）的完整数据
   * @param {Object} result - API 返回的结果
   * @param {string} link_type - 帖子类型
   * @returns {Object} 处理后的数据对象
   */
  static processNormalLinkData(result, link_type) {
    const link = result.link;
    const is_concept = link_type === 'concept';
    const is_video = link.has_video == 1;

    // 处理内容格式
    const processedLink = this.processNormalLinkContent(link, is_video);

    // 处理游戏相关信息
    const game_comment_share_info = result.game_comment_share_info || {};
    const game_info = result.game_info || {};

    // 处理点赞和收藏信息
    const awardInfo = {
      isAward: link.is_award_link,
      awardCount: link.link_award_num
    };
    const favInfo = {
      isFav: link.is_favour,
      favCount: link.favour_count
    };

    return {
      link: processedLink,
      is_concept,
      is_video,
      game_comment_share_info,
      game_info,
      awardInfo,
      favInfo
    };
  }

  /**
   * 处理文章内容，创建 Article 实例
   * @param {Object} result - API 返回的结果
   * @param {boolean} is_concept - 是否为概念图类型
   * @returns {Object} Article 实例
   */
  static processArticleContent(result, is_concept) {
    // 如果是概念图，只保留文本类型的内容
    let article_link = is_concept
      ? Object.assign({}, result.link, {
          content: result.link?.content?.filter(item => item.type == 'text') || []
        })
      : result.link;

    // 创建 Article 实例
    const article = new Article(article_link, {
      card_delegate: new CardDelegate([
        GameCards,
        SkuCards,
        WikiCards,
        AlbumCards,
        LeagueCards,
        MatchCards,
        AwardTableCards,
        ChatRoomCards,
        Dota2MatchRecordCards,
        LOLMatchRecordCards
      ]),
      base_delegate: DEFAULT_BASE_DELEGATE,
      preproccess_delegate: new PreproccessDelegate([
        handleOriginSource,
        handleOriginalNews,
        handleNewsSource
      ])
    });

    // 过滤不需要的组件
    article.content = article.content.filter((cpt) => {
      return cpt.cpt != 'ComLOLMatchRecordCard' && cpt.data.type != 'timeline';
    });

    // 处理文本组件的 emoji
    article.content.forEach(item => {
      if (item.cpt === 'ComText') {
        item.data.value = emojiRender.replaceEmojiByTextName(item.data.value || '');
      }
    });

    return article;
  }

  /**
   * 格式化评论富文本
   * @param {Object|Array} item - 评论数据，可以是 { comment: [...] } 或 [...]
   * @param {boolean} is_sub - 是否为子评论
   * @returns {void} 直接修改原对象
   */
  static formatCommentRichText(item, is_sub = false) {
    const comment = is_sub ? item : (item.comment || []);
    
    comment.forEach((c, index) => {
      // 处理 CY 标识（第一条评论且 is_cy == 1）
      if (index === 0 && c.is_cy == 1) {
        c.text = '<img class="icon-cy" src="https://static.max-c.com/heybox-chat/static/cy.svg"/>' + c.text;
      }

      // Markdown 转 HTML
      c.text = markdownToHtml(c.text || '', true);

      // 处理 URL 链接（排除 src 和 href 中的 URL）
      const urlPattern = /(https?:\/\/[^\s"]+)(?![^<>]*>)/g;
      c.text = c.text.replace(urlPattern, (url) => {
        const srcHrefPattern = new RegExp(`(src|href)=["']${url}["']`);
        if (!srcHrefPattern.test(c.text)) {
          return `<a href="${url}" target="_blank">${url}</a>`;
        }
        return url;
      });
    });
  }

  /**
   * 处理评论列表数据
   * @param {Array} comments - 评论列表
   * @param {string|number} root_comment_id - 根评论 ID（用于过滤）
   * @returns {Array} 处理后的评论列表
   */
  static processCommentList(comments = [], root_comment_id = null) {
    let processedComments = [...comments];

    // 如果指定了 root_comment_id，过滤掉对应的评论
    if (root_comment_id) {
      const index = processedComments.findIndex(
        item => item.comment[0]?.commentid == root_comment_id
      );
      if (index > -1) {
        processedComments.splice(index, 1);
      }
    }

    // 格式化每条评论的富文本
    processedComments.forEach(item => {
      this.formatCommentRichText(item);
    });

    return processedComments;
  }

  /**
   * 处理当前评论数据
   * @param {Object} current_comment - 当前评论数据
   * @returns {Array} 处理后的评论数组
   */
  static processCurrentComment(current_comment = { comment: [] }) {
    this.formatCommentRichText(current_comment);
    return current_comment?.comment || [];
  }

  /**
   * 处理子评论列表，并去重
   * @param {Array} comments - 子评论列表
   * @param {Array} existingComments - 已存在的评论列表（会被直接修改）
   * @returns {Array} 去重后的评论列表
   */
  static processSubCommentList(comments = [], existingComments = []) {
    // 格式化新评论
    this.formatCommentRichText(comments, true);

    // 将新评论添加到现有列表
    existingComments.push(...comments);

    // 去重（根据 commentid）- 使用原始的双重循环逻辑保持一致性
    for (let i = 0; i < existingComments.length; i++) {
      for (let j = i + 1; j < existingComments.length; j++) {
        if (existingComments[i].commentid === existingComments[j].commentid) {
          existingComments.splice(j, 1);
          j--; // 删除了一个元素，所以需要将 j 减 1
        }
      }
    }

    return existingComments;
  }

  /**
   * 构建评论列表请求参数
   * @param {Object} options - 参数选项
   * @param {string|number} options.link_id - 帖子 ID
   * @param {number} options.limit - 每页数量
   * @param {number} options.page - 当前页码
   * @param {string} options.sort_filter - 排序方式
   * @param {number} options.index - 索引（可选）
   * @param {string|number} options.root_comment_id - 根评论 ID（可选）
   * @returns {Object} 请求参数对象
   */
  static buildCommentListParams({
    link_id,
    limit,
    page,
    sort_filter,
    index,
    root_comment_id
  }) {
    const params = {
      link_id,
      limit,
      page,
      sort_filter,
      hide_cy: 0
    };

    // 第一页添加 is_first 标识
    if (page === 1) {
      params.is_first = 1;
    }

    // 添加索引（如果存在）
    if (typeof index === 'number') {
      params.index = index;
    }

    // 添加根评论 ID（如果存在）
    if (root_comment_id) {
      params.root_comment_id = root_comment_id;
    }

    return params;
  }

  /**
   * 统一处理帖子数据初始化
   * @param {Object} result - API 返回的结果
   * @param {string} link_type - 帖子类型 ('article' | 'concept' | 'video' | 其他)
   * @param {boolean} isArticleType - 是否为文章类型
   * @returns {Object} 处理后的数据对象
   */
  static initPostData(result, link_type, isArticleType = false) {
    if (isArticleType) {
      return this.processArticleLinkData(result);
    } else {
      return this.processNormalLinkData(result, link_type);
    }
  }
}

export default PostDataUtils;
